import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { clsx } from "clsx";
import {
  CheckCircle2, FileText, Highlighter, History, Layers, Loader2, PanelLeftOpen,
  PanelRightOpen, Pilcrow, X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { AnnotationEditor } from "../components/AnnotationEditor";
import { DocumentPreview } from "../components/DocumentPreview";
import { DocumentStrip } from "../components/DocumentStrip";
import { OfficeEditor } from "../components/OfficeEditor";
import { PageGrid } from "../components/PageGrid";
import { PageNumbersModal } from "../components/PageNumbersModal";
import { PdfChatPanel } from "../components/PdfChatPanel";
import { SessionRail } from "../components/SessionRail";
import { ToolPalette, bumpUsage, toolBlockedReason } from "../components/ToolPalette";
import { ToolParams, defaultParams } from "../components/ToolParams";
import { VersionHistory } from "../components/VersionHistory";
import {
  invalidateSession, pdfErrorText, useApplyTool, useCreateSession, useDeleteDocument,
  useDeleteSession, useDocumentTicket, usePdfSession, usePdfSessions, useRenameDocument,
  useSessionTasks, useTaskStatus, useToolCatalog, useUpdateSession, useUploadDocuments,
} from "../api/pdfApi";
import { useQueryClient } from "@tanstack/react-query";
import type { PdfDocument, PdfShape, PdfTextRun, PdfTool } from "../types";

const RAIL_KEY = "elg5.pdf.rail.open";

type Tab = "pages" | "preview" | "content" | "editor" | "history";

const TABS: { key: Tab; label: string; icon: typeof FileText }[] = [
  { key: "pages", label: "Strony", icon: Layers },
  { key: "preview", label: "Podgląd", icon: FileText },
  { key: "content", label: "Treść", icon: Pilcrow },
  { key: "editor", label: "Edytor", icon: Highlighter },
  { key: "history", label: "Historia", icon: History },
];

/**
 * Zakładki zależą od tego, CZYM jest zaznaczony plik.
 *
 * „Treść” ma sens tylko tam, gdzie treść jest zapisana wprost — w Wordzie, ODT-cie
 * i Excelu. „Edytor” (adnotacje, zaczernianie, poprawki na obrazie strony) pracuje
 * na warstwach PDF-a i na pliku biurowym nie ma czego złapać, więc go tam nie ma:
 * treść tego pliku poprawia się na zakładce obok, bez mierzenia czegokolwiek.
 */
export function tabsFor(doc: { kind: string; office_kind: string } | null): typeof TABS {
  if (!doc) return TABS;
  return TABS.filter((t) => {
    if (t.key === "content") return !!doc.office_kind;
    if (t.key === "editor") return !doc.office_kind;
    return true;
  });
}

/**
 * DLC_12 — Narzędzia PDF.
 *
 * Trzy kolumny: sprawy → pulpit z plikami i kafelkami → asystent. Kafelek
 * i asystent wywołują tę SAMĄ operację na serwerze, więc nie da się ich rozjechać;
 * asystent ma ponadto własną piaskownicę, w której pisze kod na plikach sprawy.
 */
/**
 * Poprawki treści grupowane po stronie, rosnąco. Backend przyjmuje JEDNĄ stronę na
 * wywołanie, więc robota z drugiej kartki wylądowałaby na współrzędnych pierwszej —
 * stąd osobne wywołanie na stronę, po kolei (każde daje nową wersję pliku).
 */
export function editsByPage(edits: PdfTextRun[], fallback: number): [number, PdfTextRun[]][] {
  const byPage = new Map<number, PdfTextRun[]>();
  edits.forEach((edit) => {
    const page = edit.page ?? fallback;
    byPage.set(page, [...(byPage.get(page) ?? []), edit]);
  });
  return [...byPage.entries()].sort((a, b) => a[0] - b[0]);
}

export interface SignatureLoss {
  /** `removes` — nowa wersja pliku bez podpisu; `derived` — nowe pliki bez podpisu. */
  effect: "removes" | "derived";
  names: string[];
}

/**
 * Czy operacja zgubi podpis elektroniczny wskazanych plików — i w jaki sposób.
 *
 * Każde przepisanie PDF-a unieważnia podpis, więc pytamy PRZED operacją, a nie
 * dopiero w komunikacie po niej. Narzędzie spoza katalogu traktujemy jak zmianę
 * pliku (bezpieczniej zapytać o raz za dużo). `extract_pages` z wyłączonym
 * „Zapisz jako nowy plik” oddaje wynik nową wersją wejścia — to zmiana pliku,
 * nie plik pochodny.
 */
export function signatureLoss(
  tool: Pick<PdfTool, "signature" | "params"> | undefined,
  values: Record<string, unknown>,
  docs: Pick<PdfDocument, "name" | "has_signature">[],
): SignatureLoss | null {
  if (tool?.signature === "keeps") return null;
  const names = docs.filter((d) => d.has_signature).map((d) => d.name);
  if (names.length === 0) return null;
  const inPlace =
    !!tool?.params.some((p) => p.name === "as_new_document") && values.as_new_document === false;
  return { effect: tool?.signature === "derived" && !inPlace ? "derived" : "removes", names };
}

export function PdfToolsPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("s");
  const [archived, setArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [tab, setTab] = useState<Tab>("pages");
  const [tool, setTool] = useState<PdfTool | null>(null);
  // Numerowanie ma własne okno: położenie wybiera się NA STRONIE, a nie z listy.
  const [numbersOpen, setNumbersOpen] = useState(false);
  // Okno zgody na utratę podpisu: obietnica rozstrzyga się klikiem w oknie.
  const [signatureAsk, setSignatureAsk] = useState<
    { loss: SignatureLoss; resolve: (ok: boolean) => void } | null
  >(null);
  const [toolValues, setToolValues] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(true);
  // Zwinięta lista spraw — decyzja użytkownika, więc pamiętana między wejściami.
  const [railOpen, setRailOpen] = useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) !== "0";
    } catch {
      return true;
    }
  });

  const toggleRail = (open: boolean) => {
    setRailOpen(open);
    try {
      localStorage.setItem(RAIL_KEY, open ? "1" : "0");
    } catch {
      /* tryb prywatny — zwinięcie zostaje tylko na tę sesję */
    }
  };

  const { data: sessions = [], isLoading: sessionsLoading } = usePdfSessions(archived);
  const { data: session, isLoading: sessionLoading } = usePdfSession(sessionId);
  const { data: catalog } = useToolCatalog();

  const createSession = useCreateSession();
  const updateSession = useUpdateSession();
  const deleteSession = useDeleteSession();
  const upload = useUploadDocuments();
  const apply = useApplyTool();
  const renameDoc = useRenameDocument();
  const deleteDoc = useDeleteDocument();

  const select = useCallback(
    (id: string | null) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set("s", id);
          else next.delete("s");
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // Pierwsze wejście: otwieramy ostatnią sprawę albo zakładamy pierwszą, żeby
  // nikt nie zderzył się z pustym ekranem i pytaniem „co teraz”.
  //
  // `isError` w warunku jest tu kluczowe: gdy zakładanie sprawy pada (brak modułu,
  // błąd serwera), efekt odpalał się przy każdym odświeżeniu listy i strona biła
  // w API bez końca, pokazując wieczny spinner na przycisku zamiast powodu.
  useEffect(() => {
    if (sessionId || sessionsLoading || archived) return;
    if (sessions.length > 0) select(sessions[0].id);
    else if (!createSession.isPending && !createSession.isSuccess && !createSession.isError)
      createSession.mutate(undefined, {
        onSuccess: (s) => select(s.id),
        onError: (e) => setError(pdfErrorText(e, "Nie udało się założyć sprawy.")),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, sessions, sessionsLoading, archived]);

  const documents = session?.documents ?? [];
  const selectedDocs = useMemo(
    () => documents.filter((d) => selectedIds.includes(d.id)),
    [documents, selectedIds],
  );
  const primary = selectedDocs[0] ?? documents[0] ?? null;
  const { data: ticket } = useDocumentTicket(primary?.id ?? null);

  // Zmiana sprawy = czysty stan roboczy.
  useEffect(() => {
    setSelectedIds([]);
    setSelectedPages([]);
    setTool(null);
    setResult(null);
    setError(null);
  }, [sessionId]);

  // Domyślnie zaznaczamy pierwszy plik — narzędzia mają na czym pracować od razu.
  useEffect(() => {
    if (selectedIds.length === 0 && documents.length > 0) setSelectedIds([documents[0].id]);
    if (selectedIds.length > 0) {
      const alive = selectedIds.filter((id) => documents.some((d) => d.id === id));
      if (alive.length !== selectedIds.length) setSelectedIds(alive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  useTaskStatus(taskId, (task) => {
    setTaskId(null);
    if (task.status === "success") setResult(task.message || "Gotowe.");
    else setError(task.error || "Operacja w tle nie powiodła się.");
    invalidateSession(qc, sessionId);
  });

  // Zadania w tle tej sprawy — łącznie z tymi, które zlecił asystent. Bez tego
  // robota z czatu leciała niewidzialnie i wyglądała jak brak reakcji.
  const { data: bgTasks } = useSessionTasks(sessionId);
  const wTle = (bgTasks ?? []).filter(
    (t) => t.status === "pending" || t.status === "running",
  );
  const skonczone = useRef<Set<string>>(new Set());
  // Serwer oddaje też zadania SKOŃCZONE w ostatnich minutach (żeby nie zgubić wyniku,
  // gdy odpowiedź przyjdzie tuż po odświeżeniu). Pierwszą taką listę tylko ZAPAMIĘTUJEMY,
  // bez ogłaszania: po wejściu na stronę wszystko jest dla nas „nowe”, więc błąd sprzed
  // dwóch minut wyskakiwał jako świeży — i wracał przy każdym kolejnym kliknięciu.
  // Użytkownik widział wtedy przy OCR-ze komunikat o zupełnie innym narzędziu.
  const pierwszyOdczyt = useRef(true);
  useEffect(() => {
    if (bgTasks === undefined) return;
    const cichy = pierwszyOdczyt.current;
    pierwszyOdczyt.current = false;
    bgTasks.forEach((t) => {
      if (["success", "failure", "cancelled"].includes(t.status) && !skonczone.current.has(t.id)) {
        skonczone.current.add(t.id);
        if (t.id !== taskId && !cichy) {
          // Zadanie z czatu skończone — pulpit ma pokazać nową wersję pliku.
          invalidateSession(qc, sessionId);
          if (t.status === "success") setResult(`Gotowe: ${t.display_name}`);
          else if (t.error) setError(t.error);
        }
      }
    });
  }, [bgTasks, sessionId, taskId]);

  // Zmiana sprawy = inna lista zadań; pierwszy jej odczyt też ma być cichy.
  useEffect(() => {
    pierwszyOdczyt.current = true;
    skonczone.current = new Set();
  }, [sessionId]);

  const tabs = useMemo(() => tabsFor(primary), [primary]);

  // Plik biurowy otwiera się na swojej treści, a nie na miniaturach stron: to na
  // niej robi się tu robotę. I odwrotnie — po przełączeniu na PDF-a zakładka
  // „Treść” znika, więc trzeba zejść z niej na coś, co istnieje.
  useEffect(() => {
    if (!primary) return;
    if (primary.office_kind && (tab === "pages" || tab === "editor")) setTab("content");
    if (!primary.office_kind && tab === "content") setTab("pages");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary?.id, primary?.office_kind]);

  const pickTool = (picked: PdfTool) => {
    setError(null);
    setResult(null);
    // Narzędzia biurowe prowadzą do zakładki „Treść”: adresy akapitów i komórek
    // wybiera się klikając w dokument, a nie wpisując „p12” do formularza.
    if (picked.group === "office") {
      const blocked = toolBlockedReason(picked, selectedDocs);
      setTool(null);
      if (blocked || !primary?.office_kind) {
        setError(blocked ?? "To narzędzie działa na plikach Worda, Excela i ODT.");
        return;
      }
      setTab("content");
      setResult(
        "Poprawiaj wprost w dokumencie: kliknij w akapit albo komórkę, a nowe " +
          "akapity i wiersze dopiszesz przyciskami „+”.",
      );
      return;
    }
    // Narzędzia rysunkowe (margowanie, zaczernianie) prowadzą do edytora —
    // kształtów nie da się sensownie wpisać w formularz.
    if (picked.params.some((p) => p.type === "shapes")) {
      setTool(null);
      setTab("editor");
      return;
    }
    if (picked.name === "page_numbers") {
      setTool(null);
      setNumbersOpen(true);
      return;
    }
    // Kolejność ustawia się przeciąganiem miniatur — kafelek tylko tam prowadzi,
    // zamiast prosić o wpisanie „3,1,2” po przecinku.
    if (picked.name === "reorder") {
      setTool(null);
      setTab("pages");
      setResult("Przeciągnij miniatury stron w docelowej kolejności, potem kliknij „Zapisz kolejność”.");
      return;
    }
    setTool(picked);
    setToolValues(defaultParams(picked));
  };

  /** Jedno wykonanie narzędzia. Zwraca obietnicę, żeby dało się je łańcuchować —
   * dwie operacje puszczone równolegle na tym samym pliku startują z tej samej
   * wersji i druga kasuje wynik pierwszej (edytor gubił tak notatkę przy zapisie
   * razem z poprawką tekstu).
   *
   * W wyniku jedzie, czy się udało: kto łańcuchuje operacje, musi wiedzieć, że
   * pierwsza padła. Edytor treści biurowej wypycha przed dopisaniem akapitu
   * zaległe poprawki — gdyby nie sprawdził wyniku, dopisałby akapit i wyrzucił
   * niezapisane poprawki człowieka. */
  const confirmSignatureLoss = (
    operation: string,
    values: Record<string, unknown>,
    docIds: string[],
  ): Promise<boolean> => {
    const loss = signatureLoss(
      catalog?.tools.find((t) => t.name === operation),
      values,
      documents.filter((d) => docIds.includes(d.id)),
    );
    if (!loss) return Promise.resolve(true);
    return new Promise((resolve) => setSignatureAsk({ loss, resolve }));
  };
  const answerSignatureAsk = (ok: boolean) => {
    signatureAsk?.resolve(ok);
    setSignatureAsk(null);
  };

  const runTool = async (
    operation: string,
    values: Record<string, unknown>,
    docs: string[],
    // Wywołujący, który zapytał o podpis sam (edytor, numeracja — zanim zamkną swoje
    // okno albo wyczyszczą pracę), nie pyta drugi raz przy każdej operacji łańcucha.
    opts: { signatureChecked?: boolean } = {},
  ): Promise<boolean> => {
    if (!sessionId) return false;
    if (!opts.signatureChecked && !(await confirmSignatureLoss(operation, values, docs))) {
      return false;
    }
    setError(null);
    setResult(null);
    try {
      const data = await apply.mutateAsync({
        sessionId,
        operation,
        documents: docs,
        params: values,
      });
      bumpUsage(operation);
      if (data.async && data.task_id) {
        setTaskId(data.task_id);
        setResult(`${data.label ?? "Operacja"} liczy się w tle…`);
      } else {
        setResult(data.message ?? "Gotowe.");
        setTool(null);
        setSelectedPages([]);
      }
      return true;
    } catch (e) {
      setError(pdfErrorText(e));
      return false;
    }
  };

  /** Zapis pracy z edytora: najpierw poprawki treści (rasteryzują stronę),
   * potem adnotacje — po kolei, na tym samym dokumencie. */
  const saveEditorWork = async (work: {
    page: number;
    edits: PdfTextRun[];
    shapes: PdfShape[];
    objects?: { id: string; page: number; dx?: number; dy?: number; remove?: boolean }[];
  }) => {
    if (!primary) return;
    if (work.edits.length > 0) {
      for (const [p, list] of editsByPage(work.edits, work.page)) {
        await runTool("edit_text", { page: p, edits: list }, [primary.id], { signatureChecked: true });
      }
    }
    // Obiekty strony (obrazy, rysunki) — też stroną po stronie, bo backend
    // przyjmuje jedną na wywołanie.
    if (work.objects && work.objects.length > 0) {
      const byPage = new Map<number, typeof work.objects>();
      work.objects.forEach((o) => {
        byPage.set(o.page, [...(byPage.get(o.page) ?? []), o]);
      });
      for (const [p, list] of [...byPage.entries()].sort((a, b) => a[0] - b[0])) {
        await runTool(
          "edit_objects",
          { page: p, objects: list.map(({ page: _p, ...rest }) => rest) },
          [primary.id],
          { signatureChecked: true },
        );
      }
    }
    if (work.shapes.length > 0) {
      const hasRedaction = work.shapes.some((s) => s.type === "redact");
      await runTool(
        "annotate",
        { shapes: work.shapes, redact_dpi: hasRedaction ? 200 : 150 },
        [primary.id],
        { signatureChecked: true },
      );
    }
  };

  const blocked = tool ? toolBlockedReason(tool, selectedDocs) : null;

  return (
    <div className="-mx-4 -my-4 flex h-[calc(100dvh-3.5rem)] overflow-hidden lg:-mx-6 lg:-my-6">
      {!railOpen && (
        <aside className="hidden w-9 shrink-0 flex-col items-center gap-2 border-r border-[var(--color-border)] bg-[var(--color-bg-surface)] py-2 md:flex">
          <button
            type="button"
            onClick={() => toggleRail(true)}
            title="Pokaż listę spraw"
            aria-label="Pokaż listę spraw"
            className="border border-[var(--color-border-strong)] p-1.5 text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
          >
            <PanelLeftOpen size={14} />
          </button>
          <span
            className="mt-1 select-none font-mono text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]"
            style={{ writingMode: "vertical-rl" }}
          >
            Sprawy
          </span>
        </aside>
      )}

      <aside
        className={clsx(
          "w-56 shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-bg-surface)]",
          railOpen ? "hidden md:flex" : "hidden",
        )}
      >
        <SessionRail
          sessions={sessions}
          loading={sessionsLoading}
          selectedId={sessionId}
          archived={archived}
          onArchivedChange={(v) => {
            setArchived(v);
            select(null);
          }}
          onSelect={select}
          onCreate={() =>
            createSession.mutate(undefined, {
              onSuccess: (s) => {
                setArchived(false);
                select(s.id);
              },
              onError: (e) => setError(pdfErrorText(e, "Nie udało się założyć sprawy.")),
            })
          }
          onArchive={(s, value) => updateSession.mutate({ id: s.id, archived: value })}
          onDelete={(s) => {
            deleteSession.mutate(s.id);
            if (s.id === sessionId) select(null);
          }}
          onRename={(s, title) => updateSession.mutate({ id: s.id, title })}
          onCollapse={() => toggleRail(false)}
          creating={createSession.isPending}
        />
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-2">
          <div className="flex min-w-0 items-baseline gap-3">
            <h1 className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
              {session?.title ?? "Narzędzia PDF"}
            </h1>
            <span className="section-label shrink-0">DLC_12</span>
          </div>
          <div className="flex items-center gap-3">
            {(apply.isPending || taskId || wTle.length > 0) && (
              <span
                title={wTle.map((t) => t.display_name).join(", ")}
                className="flex items-center gap-1.5 font-mono text-[11px] text-[var(--color-text-muted)]"
              >
                <Loader2 size={12} className="animate-spin" />
                {wTle.length > 0
                  ? `w tle: ${wTle[0].display_name}${wTle.length > 1 ? ` (+${wTle.length - 1})` : ""}`
                  : taskId
                    ? "liczę w tle…"
                    : "pracuję…"}
              </span>
            )}
            {!chatOpen && (
              <button
                type="button"
                onClick={() => setChatOpen(true)}
                className="section-label flex items-center gap-1 hover:text-[var(--color-primary)]"
              >
                <PanelRightOpen size={13} /> Asystent
              </button>
            )}
          </div>
        </header>

        {sessionLoading && (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        )}

        {session && (
          <>
            <DocumentStrip
              documents={documents}
              selectedIds={selectedIds}
              onSelect={(ids) => {
                setSelectedIds(ids);
                setSelectedPages([]);
              }}
              onUpload={(files) =>
                upload.mutate(
                  { sessionId: session.id, files },
                  {
                    onSuccess: (docs) => {
                      if (docs[0]) setSelectedIds([docs[0].id]);
                      setResult(`Wgrano ${docs.length} plik(i).`);
                    },
                    onError: (e) => setError(pdfErrorText(e, "Nie udało się wgrać pliku.")),
                  },
                )
              }
              onRename={(doc, name) =>
                renameDoc.mutate({ documentId: doc.id, name, sessionId: session.id })
              }
              onDelete={(doc) => deleteDoc.mutate({ documentId: doc.id, sessionId: session.id })}
              onHistory={(doc) => {
                setSelectedIds([doc.id]);
                setTab("history");
              }}
              onError={setError}
              uploading={upload.isPending}
            />

            {(result || error) && (
              <div
                className={clsx(
                  "flex items-start gap-2 border-b px-4 py-1.5 text-[11px]",
                  error
                    ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                    : "border-[var(--color-border)] bg-[var(--color-bg-elevated)]",
                )}
              >
                {error ? (
                  <X size={13} className="mt-px shrink-0 text-[var(--color-primary)]" />
                ) : (
                  <CheckCircle2 size={13} className="mt-px shrink-0 text-[var(--color-text-muted)]" />
                )}
                <span className="min-w-0 flex-1 whitespace-pre-wrap text-[var(--color-text-primary)]">
                  {error || result}
                </span>
                <button
                  type="button"
                  aria-label="Zamknij komunikat"
                  onClick={() => {
                    setError(null);
                    setResult(null);
                  }}
                  className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                >
                  <X size={13} />
                </button>
              </div>
            )}

            <div className="flex min-h-0 flex-1">
              <section className="flex w-[300px] shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-bg-surface)]">
                <div className="min-h-0 flex-1">
                  <ToolPalette
                    catalog={catalog}
                    selectedDocs={selectedDocs}
                    activeTool={tool?.name ?? null}
                    onPick={pickTool}
                  />
                </div>
                {tool && (
                  <ToolParams
                    tool={tool}
                    documents={selectedDocs}
                    selectedPages={selectedPages}
                    values={toolValues}
                    onChange={setToolValues}
                    onRun={() =>
                      blocked
                        ? setError(blocked)
                        : runTool(tool.name, toolValues, selectedDocs.map((d) => d.id))
                    }
                    onClose={() => setTool(null)}
                    running={apply.isPending}
                    error={blocked}
                  />
                )}
              </section>

              <section className="flex min-w-0 flex-1 flex-col">
                <nav className="flex items-center gap-0 border-b border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2">
                  {tabs.map((t) => {
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setTab(t.key)}
                        aria-pressed={tab === t.key}
                        className={clsx(
                          "flex items-center gap-1.5 border-b-2 px-3 py-2 font-mono text-[11px] uppercase tracking-wider transition-colors duration-100",
                          tab === t.key
                            ? "border-b-[var(--color-primary)] text-[var(--color-primary)]"
                            : "border-b-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]",
                        )}
                      >
                        <Icon size={13} /> {t.label}
                      </button>
                    );
                  })}
                </nav>

                <div className="min-h-0 flex-1 bg-[var(--color-bg-base)]">
                  {!primary && (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
                      <FileText size={28} className="text-[var(--color-border-strong)]" />
                      <p className="text-xs text-[var(--color-text-muted)]">
                        Przeciągnij pliki na pulpit, żeby zacząć.
                      </p>
                    </div>
                  )}
                  {primary && tab === "pages" && (
                    <PageGrid
                      document={primary}
                      ticket={ticket}
                      selected={selectedPages}
                      onSelect={setSelectedPages}
                      onOpenPage={() => setTab("preview")}
                      onReorder={(order) =>
                        runTool("reorder", { order: order.join(",") }, [primary.id])
                      }
                      reordering={apply.isPending}
                    />
                  )}
                  {primary && tab === "preview" && (
                    <DocumentPreview document={primary} ticket={ticket} />
                  )}
                  {primary && tab === "content" && primary.office_kind && (
                    <OfficeEditor
                      document={primary}
                      saving={apply.isPending}
                      onApply={(operation, values) => runTool(operation, values, [primary.id])}
                    />
                  )}
                  {primary && tab === "editor" && (
                    <AnnotationEditor
                      document={primary}
                      ticket={ticket}
                      onSave={saveEditorWork}
                      confirmSave={() => confirmSignatureLoss("annotate", {}, [primary.id])}
                      saving={apply.isPending}
                    />
                  )}
                  {primary && tab === "history" && (
                    <VersionHistory document={primary} sessionId={session.id} />
                  )}
                </div>
              </section>
            </div>
          </>
        )}
      </main>

      {primary && numbersOpen && (
        <PageNumbersModal
          document={primary}
          ticket={ticket}
          open={numbersOpen}
          onClose={() => setNumbersOpen(false)}
          running={apply.isPending}
          onRun={async (params) => {
            // Pytamy, zanim okno numeracji się zamknie — odmowa zostawia ustawienia.
            if (!(await confirmSignatureLoss("page_numbers", params, [primary.id]))) return;
            await runTool("page_numbers", params, [primary.id], { signatureChecked: true });
            setNumbersOpen(false);
          }}
        />
      )}

      {signatureAsk && (
        <Modal open onClose={() => answerSignatureAsk(false)} title="Plik ma podpis elektroniczny" size="sm">
          <div className="space-y-3 text-sm">
            {signatureAsk.loss.effect === "removes" ? (
              <>
                <p>
                  {signatureAsk.loss.names.map((n) => `„${n}”`).join(", ")}{" "}
                  {signatureAsk.loss.names.length > 1 ? "mają" : "ma"} podpis elektroniczny. Ta
                  operacja zapisze <b>nową wersję bez podpisu</b> — każda zmiana w podpisanym
                  PDF-ie go unieważnia.
                </p>
                <p className="text-[var(--color-text-muted)]">
                  Podpisana wersja zostaje w historii pliku i da się ją przywrócić. Jeśli plik
                  ma dalej iść jako podpisany, po zmianie trzeba go podpisać ponownie.
                </p>
              </>
            ) : (
              <p>
                {signatureAsk.loss.names.map((n) => `„${n}”`).join(", ")}{" "}
                {signatureAsk.loss.names.length > 1 ? "mają" : "ma"} podpis elektroniczny. Plik
                źródłowy zostaje nietknięty, ale <b>nowe pliki z tej operacji nie będą
                podpisane</b>.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => answerSignatureAsk(false)}>Anuluj</Button>
              <Button variant="warning" onClick={() => answerSignatureAsk(true)}>Wykonaj mimo to</Button>
            </div>
          </div>
        </Modal>
      )}

      {session && chatOpen && (
        <aside className="hidden w-[380px] shrink-0 flex-col border-l border-[var(--color-border)] bg-[var(--color-bg-surface)] xl:flex">
          <PdfChatPanel
            sessionId={session.id}
            onTurnFinished={() => invalidateSession(qc, session.id)}
            onCollapse={() => setChatOpen(false)}
          />
        </aside>
      )}
    </div>
  );
}

export default PdfToolsPage;
